import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import moment from 'moment';
import { compact } from 'lodash';
import { useDataContext } from '../../context/data.js';
import { useStampWallet } from '../../context/stampWallet.js';
import { formatTheaterPerformanceLabel } from '../../utils/theater';
import { computeDiscountedTotal } from '../../utils/stampcard';
import { isWoltFeePending } from '../../utils/woltDelivery';
import translations from '../../constants/translations.json';
import LoyaltyBanner from './LoyaltyBanner';
import VoucherRedeemBlock from './VoucherRedeemBlock';
import StampProgressCard from './StampProgressCard';
import './YourItems.css';

const YourItems = forwardRef(
  (
    {
      restaurant,
      categories,
      items,
      // Lines the venue cannot serve right now, from the cart's stock check:
      // [{ _id, name, type, available, requested }]. Empty at venues without
      // stock control, so nothing below renders for them.
      soldOutItems = [],
      onUpdateQuantity,
      onRemoveItem,
      onEditItem,
      tipPercentage,
      tipAmount,
      discountCode,
      discountAmount = 0,
      total,
      grandTotal,
      // The grand total the guest would pay WITHOUT the discount code, for the
      // struck-through figure. It has to be passed separately: the code lowers
      // the base for the tip and the service fee too, so the pre-code total
      // cannot be recovered by adding discountAmount back on. Optional — a
      // caller that does not pass it falls back to `grandTotal`.
      grandTotalBeforeCode,
      calculatedServiceFee,
      currency = 'DKK',
      onPayment,
      defaultImg,
      orderType,
      hasBag,
      bagFee,
      hasDeliveryFee,
      deliveryFee,
      woltDelivery,
      woltDeliveryFee,
      hasServiceFee,
      serviceFee,
      hasFlatFee,
      flatFee,
      pickupDate = null,
      pickupTime = '',
      pickup,
      // Theater mode: the chosen performance object (null when none chosen) —
      // echoed as a summary row instead of the pickup date/time row.
      theaterPerformance = null,
      // The guest is on the pre-order flow, not merely at a venue that has one.
      isTheaterPreorder = false,
      // The other theater flow: an immediate order that names a performance.
      isTheaterOrderNow = false,
      currentStep,
      onGoToSelectPaymentMethod,
      loyaltyEmail,
      loyaltyPhone,
      onLoyaltyDiscount,
    },
    ref,
  ) => {
    let pathname = window.location.pathname;
    const restaurantId = pathname.split('/')?.[2];
    const { language } = useDataContext();

    // Stampcard overview (§9.2): per campaign, what the cart EARNS — live
    // progress + potential stamps from this order (honestly capped by the
    // campaign's per-order limit). Fail-safe-to-nothing when no wallet
    // provider is mounted or nothing in the cart qualifies.
    const { progressByProduct = {} } = useStampWallet() || {};
    const stampOverview = useMemo(() => {
      const byCampaign = {};
      (items || []).forEach((it) => {
        const pid = String(it?._id || it?.id || '');
        const sp = progressByProduct?.[pid];
        const maxCount = Number(sp?.maxCount);
        if (!sp || !Number.isFinite(maxCount) || maxCount <= 0) return;
        const key = String(sp.stampId || sp.stampName || pid);
        if (!byCampaign[key]) {
          byCampaign[key] = {
            key,
            name: sp.stampName,
            maxCount,
            current: (Number(sp.currentCount) || 0) % maxCount,
            cap: sp.stampsPerOrderCap != null ? Number(sp.stampsPerOrderCap) : null,
            qty: 0,
          };
        }
        byCampaign[key].qty += Math.max(1, Number(it?.quantity) || 1);
      });
      return Object.values(byCampaign).map((c) => {
        const potential = c.cap != null ? Math.min(c.qty, c.cap) : c.qty;
        return {
          ...c,
          potential,
          after: Math.min(c.current + potential, c.maxCount),
          completes: c.current + potential >= c.maxCount,
        };
      });
    }, [items, progressByProduct]);

    const [itemsWithTimeErrors, setItemsWithTimeErrors] = useState([]);
    const [loyaltyDiscount, setLoyaltyDiscount] = useState(0); // percent
    // Staged-voucher reward value (absolute currency amount, server-authoritative
    // at checkout — this is display-only, mirroring the loyalty discount line).
    const [voucherDiscountAmount, setVoucherDiscountAmount] = useState(0);
    // The voucher comes off BEFORE the loyalty percentage is taken, mirroring the
    // server's order of operations — see utils/stampcard.js. With no voucher
    // staged this is exactly the old `(total * pct) / 100` / `grandTotal - amount`.
    const { loyaltyDiscountAmount, adjustedGrandTotal } = computeDiscountedTotal({
      total,
      grandTotal,
      loyaltyPercent: loyaltyDiscount,
      voucherDiscount: voucherDiscountAmount,
    });

    // The discount-code amount is the SERVER's number, handed down from the page
    // after /discountcode/validate answered — this component never derives it.
    const discountCodeAmount = Math.max(0, Number(discountAmount) || 0);
    const hasDiscount = loyaltyDiscount > 0 || discountCodeAmount > 0 || voucherDiscountAmount > 0;
    // How many discount rows are on screen, so the wrapper can give the Total
    // enough room to stay above the fold on short viewports.
    const discountRowCount =
      (loyaltyDiscount > 0 ? 1 : 0) +
      (discountCodeAmount > 0 ? 1 : 0) +
      (voucherDiscountAmount > 0 ? 1 : 0);
    // `grandTotal` already arrives net of the code (the page's calculateTotal
    // subtracts it before the tip and the fees), so the struck-through "before"
    // figure is the separately-passed one; fall back to grandTotal when no code
    // is applied, and when an older caller passes no such prop at all.
    const grandTotalBeforeDiscounts =
      discountCodeAmount > 0 && grandTotalBeforeCode != null
        ? Number(grandTotalBeforeCode)
        : Number(grandTotal) || 0;

    // Helper function to get the proper image source
    const getItemImage = (item) => {
      return item.image || item.restaurant?.MenuPlaceholder || defaultImg;
    };

    const isTakeAway = orderType === 'take-away' || orderType === 'delivery';

    // Validate if pickup time is within the category's available time range
    const validateItemTimeAvailability = (item, pickupDate, pickupTime) => {
      const category = categories.find((cat) => cat._id === item.higherCategoryId);
      // Skip validation if not take-away or no category time constraints or no pickup time
      if (!isTakeAway || !category?.start || !category?.end || !pickupTime || !pickupDate) {
        return true;
      }

      const today = new Date();
      const selectedDate = new Date(pickupDate);

      if (selectedDate.setHours(0, 0, 0, 0) !== today.setHours(0, 0, 0, 0)) {
        return true;
      }

      // Convert pickup time to minutes for easier comparison
      const [pickupHours, pickupMinutes] = pickupTime.split(':').map(Number);
      const pickupTimeInMinutes = pickupHours * 60 + pickupMinutes;

      // Convert category start time to minutes
      const [startHours, startMinutes] = category.start.split(':').map(Number);
      const startInMinutes = startHours * 60 + startMinutes;

      // Convert category end time to minutes
      const [endHours, endMinutes] = category.end.split(':').map(Number);
      const endInMinutes = endHours * 60 + endMinutes;

      // Check if pickup time is within the allowed range
      return pickupTimeInMinutes >= startInMinutes && pickupTimeInMinutes <= endInMinutes;
    };

    // Run validation whenever items or pickup time changes
    useEffect(() => {
      if (isTakeAway && pickupTime && pickupDate) {
        const invalidItems = items.filter(
          (item) => !validateItemTimeAvailability(item, pickupDate, pickupTime),
        );
        setItemsWithTimeErrors(invalidItems);
      } else {
        setItemsWithTimeErrors([]);
      }
    }, [items, pickupTime, pickupDate, isTakeAway]);

    useImperativeHandle(ref, () => ({
      validateTimeAvailability: () => {
        // Return false if there are any time errors
        const isValid = itemsWithTimeErrors.length === 0;
        if (!isValid) {
          // You could also return more detailed error info here if needed
          return {
            isValid: false,
            errorMessage: `${itemsWithTimeErrors.length} ${translations[language]?.your_items_pickup_time_items_unavailable}`,
          };
        }
        return { isValid: true };
      },
    }));

    return (
      <div className="your-items-container w-full md:w-[300px] lg:w-[400px] xl:w-[450px] rounded-none md:rounded-xl space-y-2">
        <div className="!px-3 !py-6 lg:!px-4 lg:!py-7 border-b border-[#f5f5f5] flex justify-between items-center">
          <h2 className="text-base sm:text-lg md:text-xl font-bold">
            {translations[language]?.your_items_title}
          </h2>
          <span className="text-base sm:text-lg md:text-xl font-bold">
            {items.length} {translations[language]?.item_length_title}
          </span>
        </div>

        <div className="your-items-wrapper space-y-2">
          {/* Loyalty Banner */}
          {items.length > 0 && (
            <div className="!px-3 lg:!px-4">
              <LoyaltyBanner
                restaurant={restaurant}
                userProfile={JSON.parse(localStorage.getItem('user_profile'))}
                email={loyaltyEmail}
                phone={loyaltyPhone}
                onDiscountApplied={(pct) => {
                  setLoyaltyDiscount(pct);
                  if (onLoyaltyDiscount) onLoyaltyDiscount(pct);
                }}
              />
            </div>
          )}

          {/* Stampcard overview (§9.2): what this order earns, per campaign —
            rendered as punch-card style StampProgressCards. */}
          {items.length > 0 && stampOverview.length > 0 && (
            <div className="!px-3 lg:!px-4 pt-3 space-y-2">
              {stampOverview.map((c) => (
                <StampProgressCard
                  key={c.key}
                  name={c.name}
                  current={c.current}
                  after={c.after}
                  maxCount={c.maxCount}
                  potential={c.potential}
                  capNote={
                    c.cap != null && c.qty > c.cap
                      ? `${translations[language]?.stamp_cap_note || 'max'} ${c.cap} ${translations[language]?.stamp_per_order || 'pr. ordre'}`
                      : null
                  }
                  completes={c.completes}
                />
              ))}
            </div>
          )}

          {/* In-cart voucher redemption (reward picker). Renders nothing when there
            are no redeemable vouchers for this restaurant (fail-safe-to-nothing). */}
          {items.length > 0 && (
            <div className="!px-3 lg:!px-4">
              <VoucherRedeemBlock
                restaurant={restaurant}
                currency={currency}
                items={items}
                onVoucherDiscount={setVoucherDiscountAmount}
              />
            </div>
          )}

          {items.map((item) => {
            const category = categories.find((cat) => cat._id === item.higherCategoryId);
            // Check if this item has a time availability error
            const hasTimeError = itemsWithTimeErrors.some(
              (errorItem) => errorItem.cartId === item.cartId,
            );
            // Matched on the menu id, not the cart line: the same item can sit on
            // two lines with different extras, and the shortfall is against the
            // basket's total appetite for it, so both lines are marked.
            const short = (soldOutItems || []).find(
              (entry) => String(entry._id) === String(item._id) && entry.type !== 'extra',
            );

            return (
              <div
                key={item.cartId}
                className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] space-y-4"
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm lg:text-base font-bold">{item.name}</h3>
                      <button
                        onClick={() => onEditItem(item)}
                        className="text-sm lg:text-base text-primary hover:underline font-bold"
                      >
                        {translations[language]?.edit}
                      </button>
                    </div>

                    {/* Stock: the venue cannot serve this many right now. The
                        guest fixes it with the quantity buttons just below —
                        deliberately not removed for them. */}
                    {short && (
                      <p className="text-red-500 text-sm mt-1">
                        {Number(short.available) <= 0
                          ? translations[language]?.your_items_sold_out || 'Sold out'
                          : (
                              translations[language]?.your_items_only_left ||
                              'Only {available} left'
                            ).replace('{available}', Number(short.available))}
                      </p>
                    )}

                    {/* Show time error for this specific item */}
                    {hasTimeError && (
                      <p className="text-red-500 text-sm mt-1">
                        {translations[language]?.your_items_availabilty_from} {category?.start}{' '}
                        {translations[language]?.your_items_availabilty_to} {category?.end}
                      </p>
                    )}

                    <div className="flex items-center">
                      <button
                        onClick={() => onUpdateQuantity(item.cartId, item.quantity - 1)}
                        className="w-[16px] h-[16px] flex items-center justify-center rounded-full p-1 bg-gray-900 hover:bg-gray-500 transition-colors text-white text-sm font-bold"
                        aria-label="Remove item"
                      >
                        <i className="fa fa-minus text-[8px]" />
                      </button>
                      <span className="mx-2 text-sm font-bold">
                        {item.quantity} x {currency} {item.price}
                      </span>
                      <button
                        onClick={() => onUpdateQuantity(item.cartId, item.quantity + 1)}
                        className="w-[16px] h-[16px] flex items-center justify-center rounded-full p-1 bg-gray-900 hover:bg-gray-500 transition-colors text-white text-sm font-bold"
                        aria-label="Add item"
                      >
                        <i className="fa fa-plus text-[8px]" />
                      </button>
                    </div>
                  </div>
                  <div className="w-[70px] h-[40px] sm:w-[83px] sm:h-[50px] rounded-[5px] overflow-hidden sm:ml-4">
                    <img
                      src={getItemImage(item)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultImg || '';
                      }}
                    />
                  </div>
                </div>

                {/* Extras */}
                {item.extras && item.extras.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-[#707070]">
                      {translations[language]?.your_items_options}:
                    </p>
                    {item.extras.map((extra) => (
                      <div key={extra.id} className="flex justify-between text-xs">
                        <span className="text-[#707070]">
                          {extra.name} {Number(extra?.quantity) > 1 && `${extra.quantity}x`}
                        </span>
                        <span className="text-[#707070]">
                          {currency}{' '}
                          {(Number(extra?.quantity || 1) * Number(extra.price)).toFixed(0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Beer / Additional items */}
                {item.selectedAttributes && item.selectedAttributes.length > 0 && (
                  <>
                    {item.selectedAttributes.map((attr) => {
                      // Check if this is the "Beer" attribute (or similar)
                      const isBeer =
                        attr.attributeName.toLowerCase().includes('beer') ||
                        attr.attributeName.toLowerCase().includes('drink');
                      const options = compact(attr.options);

                      return (
                        <div key={attr.id}>
                          <p className="text-xs font-bold text-[#707070]">
                            {isBeer
                              ? `${translations[language]?.your_items_beer}:`
                              : `${attr.attributeName}:`}
                          </p>
                          {options &&
                            options.length > 0 &&
                            options.map((option) => (
                              <div key={option.id} className="flex justify-between text-xs">
                                <span className="text-[#707070]">
                                  {option?.name}{' '}
                                  {Number(option?.quantity || 1) > 1 && `${option.quantity}x`}
                                </span>
                                <span className="text-[#707070]">
                                  {currency}{' '}
                                  {(
                                    (attr?.connectedFromId
                                      ? Number(option?.Price || 0)
                                      : Number(option?.additionalPrice || 0)) *
                                    Number(option?.quantity || 1)
                                  )?.toFixed(0)}
                                </span>
                              </div>
                            ))}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Item Comments */}
                {item.comments && (
                  <div>
                    <div className="text-sm font-bold text-gray-600">
                      {translations[language]?.your_items_comments}
                    </div>
                    <div className="text-sm">{item.comments}</div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Guest discount code. Sits above Tips so the reading order matches
              the money order: items → discount → tips → fees. The Tips row below
              prints its percentage of `total`, which already arrives net of the
              code, so it needs no change of its own. */}
          {discountCodeAmount > 0 && (
            <div
              className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold"
              style={{ color: '#6f9800' }}
            >
              <span
                className="text-sm lg:text-base"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <span style={{ fontSize: 14 }}>🏷️</span>{' '}
                {translations[language]?.discount_code_row || 'Rabatkode'}
                {discountCode ? ` ${discountCode}` : ''}
              </span>
              <span className="text-sm lg:text-base">
                - {currency} {discountCodeAmount.toFixed(2)}
              </span>
            </div>
          )}

          {/* Tips Section */}
          <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
            <span className="text-sm lg:text-base">{translations[language]?.your_items_tips}</span>
            <span className="text-sm lg:text-base">
              {parseFloat(tipPercentage) * 100}% of {total.toFixed(2)} = {currency}{' '}
              {tipAmount.toFixed(2)}
            </span>
          </div>

          {/* Carry Bag Section - Only for take-away */}
          {isTakeAway && hasBag && (
            <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
              <span className="text-sm lg:text-base">
                {translations[language]?.your_items_carry_bag}
              </span>
              <span className="text-sm lg:text-base">
                1 x {currency} {bagFee}
              </span>
            </div>
          )}

          {/* Delivery Fee Section - Only for take-away */}
          {isTakeAway && hasDeliveryFee && (
            <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
              <span className="text-sm lg:text-base">
                {translations[language]?.your_items_delivery_fee}
              </span>
              <span className="text-sm lg:text-base">
                1 x {currency} {deliveryFee || 0}
              </span>
            </div>
          )}

          {/* Wolt Delivery Fee Section - Only for delivery. Until there is a
              quote there is no fee to show — "0" read as free delivery. */}
          {orderType === 'delivery' && restaurant?.EnableWoltDelivery && (
            <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
              <span className="text-sm lg:text-base">
                {translations[language]?.your_items_delivery_fee}
              </span>
              <span className="text-sm lg:text-base">
                {woltDeliveryFee === '' || woltDeliveryFee == null
                  ? translations[language]?.wolt_delivery_fee_pending || 'Calculated after address'
                  : `${woltDelivery?.price?.currency || currency} ${Number(woltDeliveryFee).toFixed(2)}`}
              </span>
            </div>
          )}

          {/* Loyalty Discount */}
          {loyaltyDiscount > 0 && (
            <div
              className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold"
              style={{ color: '#6f9800' }}
            >
              <span
                className="text-sm lg:text-base"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <span style={{ fontSize: 14 }}>⭐</span>{' '}
                {translations[language]?.loyalty_member_discount || 'Medlemsrabat'}{' '}
                {loyaltyDiscount}%
              </span>
              <span className="text-sm lg:text-base">
                - {currency} {loyaltyDiscountAmount.toFixed(2)}
              </span>
            </div>
          )}

          {/* Voucher Reward Discount (staged in-cart reward — server-authoritative
              at checkout; shown here mirroring the loyalty discount line) */}
          {voucherDiscountAmount > 0 && (
            <div
              className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold"
              style={{ color: '#6f9800' }}
            >
              <span
                className="text-sm lg:text-base"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <span style={{ fontSize: 14 }}>🎁</span>{' '}
                {translations[language]?.voucher_reward_discount || 'Belønningsrabat'}
              </span>
              <span className="text-sm lg:text-base">
                - {currency} {voucherDiscountAmount.toFixed(2)}
              </span>
            </div>
          )}

          {/* Flat Fee Section */}
          {hasFlatFee && flatFee && (
            <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
              <span className="text-sm lg:text-base">
                {translations[language]?.your_items_flat_fee}
              </span>
              <span className="text-sm lg:text-base">
                {currency} {flatFee?.toFixed(2) || '0'}
              </span>
            </div>
          )}

          {/* Service and Packaging Fee Section */}
          {hasServiceFee && Boolean(calculatedServiceFee) && (
            <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
              <span className="text-sm lg:text-base">
                {restaurant?.ServiceFeeLabel || translations[language]?.your_items_service_fee}
              </span>
              <span className="text-sm lg:text-base">
                {currency} {calculatedServiceFee?.toFixed(2) || '0'}
              </span>
            </div>
          )}

          {/* Theater performance for take-away pre-orders (replaces the pickup
              date/time row — the pre-order flow doesn't use the pickup toggle) */}
          {(isTakeAway ? isTheaterPreorder : isTheaterOrderNow) && theaterPerformance && (
            <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
              <span className="text-sm lg:text-base">
                {translations[language]?.your_items_theater_row}
              </span>
              <span className="text-sm lg:text-base">
                {formatTheaterPerformanceLabel(theaterPerformance, language)}
              </span>
            </div>
          )}

          {/* Pickup Date & Time for Take away */}
          {isTakeAway &&
            !isTheaterPreorder &&
            restaurant.allowAdvanceOrder &&
            pickupDate &&
            pickupTime && (
              <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 border-b border-[#f5f5f5] flex justify-between items-center font-bold">
                <span className="text-sm lg:text-base">
                  {orderType === 'delivery'
                    ? translations[language]?.delivery_time
                    : translations[language]?.pickup_time}
                </span>
                <span className="text-sm lg:text-base">
                  {moment(pickupDate).format('DD/MM/YYYY')} {pickupTime}
                </span>
              </div>
            )}

          {/* Total Payment */}
          <div className="!px-3 !py-3 lg:!px-4 lg:!py-4 flex justify-between items-center font-bold">
            <span className="text-sm lg:text-base">{translations[language]?.total}</span>
            <span className="text-sm lg:text-base">
              {isWoltFeePending(orderType, restaurant, woltDeliveryFee) ? (
                translations[language]?.wolt_delivery_fee_pending || 'Calculated after address'
              ) : hasDiscount ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em' }}
                  >
                    {currency} {grandTotalBeforeDiscounts.toFixed(2)}
                  </span>
                  <span style={{ color: '#6f9800' }}>
                    {currency} {adjustedGrandTotal.toFixed(2)}
                  </span>
                </span>
              ) : (
                <>
                  {currency} {grandTotal.toFixed(2)}
                </>
              )}
            </span>
          </div>

          {currentStep === 'payment' && restaurantId !== '682d79a98f4810001ac860e1' && (
            <div className="flex justify-center items-center pt-4">
              <button
                onClick={onGoToSelectPaymentMethod}
                className="flex justify-center items-center w-full space-x-2 font-bold bg-primary text-white px-8 py-[18px] lg:py-[26px] rounded-b-xl hover:bg-secondary transition-colors text-sm lg:text-base"
              >
                <i className="fa inline md:hidden fa-arrow-down" />
                <i className="fa hidden md:inline fa-arrow-left" />
                <span>{translations[language]?.your_items_select_payment_method}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default YourItems;
